import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  useStripe,
  useElements,
  CardElement,
} from "@stripe/react-stripe-js";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { get, push, ref, set, update } from "firebase/database";
import { database } from "../../../firebase";

// Load your Stripe public key
const stripePromise = loadStripe(
  "pk_test_51IwfxSCX4QmyJKkto49f99PJOD6mljlwVbDgcCHQ4tV5yMkRKsXYNRRxXcJQgFzjCf3KuChY3Ee4MmpqCGrhPviP00ad9Cn5Rn"
);

const PaymentForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userCount, totalAmount, company, user } = location.state;

  const stripe = useStripe();
  const elements = useElements();

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handlePayment = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");

    // Convert the totalAmount to cents (for USD)
    const amountInCents = totalAmount * 100; // Stripe requires amount in cents

    // Ensure that totalAmount is at least 50 cents (or equivalent in other currencies)
    if (amountInCents < 50) {
      setMessage("The minimum amount for payment is $0.50 USD.");
      setIsLoading(false);
      return;
    }

    try {
      // Replace with your Firebase Cloud Function endpoint
      const apiUrl =
        "https://us-central1-md-digital-card.cloudfunctions.net/api/create-payment-intent";

      // Create PaymentIntent on the backend
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: amountInCents, // Send the amount in cents
          currency: "aud",
          receipt_email: user.email,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        toast.error(errorData.error || "Failed to create PaymentIntent");
      }

      const { clientSecret } = await response.json();

      // Confirm the payment using Stripe.js
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (result.error) {
        // Display error message from Stripe
        setMessage(result.error.message);
      } else if (result.paymentIntent.status === "succeeded") {
        // Payment succeeded
        const billsRef = push(ref(database, "bills"));
        const billsId = billsRef.key;
        await set(billsRef, {
          Id: billsId,
          companyId: company,
          uid: user?.uid,
          userNumber: userCount,
          Amount: totalAmount,
          paymentId: result.paymentIntent.id,
          paymentStatus: result.paymentIntent.status,
          createdAt: new Date().toISOString(),
          bill_details: result.paymentIntent,
        });
        console.log("bill_details", result);
        if (company) {
          const companyRef = ref(database, `companies/${company}`);
          const companySnapshot = await get(companyRef);
          if (companySnapshot.exists()) {
            const companyData = companySnapshot.val();
            let expiredAtDate = new Date(companyData.expiredAt);
            expiredAtDate.setMonth(expiredAtDate.getMonth() + 1);
            const updatedData = {             
              expiredAt: expiredAtDate.toISOString(),
            };
            await update(companyRef, updatedData);

          } else {
            console.log("Company not found!");
          }
        }
        toast.success("Payment successful!");
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error during payment:", error.message);
      toast.error("Payment failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <form
        onSubmit={handlePayment}
        className="w-full max-w-sm p-6 bg-white rounded shadow-md"
      >
        <h2 className="text-xl font-bold text-center mb-4">Checkout</h2>

        {/* Display Amount */}
        <div className="mb-4">
          <label
            htmlFor="amount"
            className="block text-sm font-medium text-gray-700"
          >
            Amount (in AUD):
          </label>
          <input
            id="amount"
            type="number"
            value={totalAmount.toFixed(2)}
            className="mt-1 p-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            disabled
          />
        </div>

        {/* CardElement for card input */}
        {/* <CardNumberElement />
        <CardCvcElement/> */}

        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                color: "#424770",
                "::placeholder": { color: "#aab7c4" },
              },
              invalid: { color: "#9e2146" },
            },
          }}
          className="mt-2 p-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />

        <button
          type="submit"
          disabled={!stripe || isLoading}
          className="w-full mt-4 px-4 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? "Processing..." : "Pay"}
        </button>

        {message && (
          <p
            className={`mt-4 text-center text-sm ${
              message === "Payment successful!"
                ? "text-green-600"
                : "text-red-600"
            }`}
          >
            {message}
          </p>
        )}
      </form>
    </div>
  );
};

const App = () => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm />
    </Elements>
  );
};

export default App;
